import { FC, useState } from 'react';
import { AnalyzeResponse } from '../types/analyzeResponse';
import ProgressBar from './ProgressBar';
import './analysisAIResults.css';

interface Props {
  result: AnalyzeResponse;
}

export const AnalysisAIResultPanel: FC<Props> = ({ result }) => {
  const [skillsTab, setSkillsTab] = useState<'covered' | 'gaps'>('covered');
  const [showClarity, setShowClarity] = useState(false);
  const [showSignal, setShowSignal] = useState(false);

  return (
    <div className="hero-card" style={{ marginTop: 24 }}>
      <div className="card">
        <div className="card-header">Analysis Results</div>

        <div className="card-body">

            {/* SECTION 1 — Overall Fit */}
            <ProgressBar label="Domain Alignment" value={result.scores.domain_alignment} />
            <ProgressBar label="Role Alignment" value={result.scores.role_alignment} />
            <ProgressBar label="Seniority Alignment" value={result.scores.seniority_alignment} />
            <br />
            <hr style={{borderColor: "#475569"}} />
            <br />
            <ProgressBar label="Fit Score" value={result.bars.fit_score} />
            <div style={{ color: '#9ca3af', fontSize: 13 }}>
                High confidence match
            </div>

            {result.explain.fit_reasons.length > 0 && (
                <>
                <h4 style={{ marginTop: 16 }}>Why this is a good fit</h4>
                <ul className="reason-list">
                    {result.explain.fit_reasons.slice(0, 3).map((r, i) => (
                    <li key={i}>
                        <strong>{r.label}</strong>
                        {r.resume_quote && <div className="quote">“{r.resume_quote}”</div>}
                    </li>
                    ))}
                </ul>
                </>
            )}
            <br />
            <hr style={{borderColor: "#475569"}} />
            <br />
            {/* SECTION 2 — Skills */}
            <h3>Skills Match</h3>
            <ProgressBar label="Skills Match" value={result.bars.skills_match} />

            <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
                <div>Required: <b>{result.scores.required_skills.with_evidence}</b> / {result.scores.required_skills.total}</div>
                <div>Preferred: <b>{result.scores.preferred_skills.with_evidence}</b> / {result.scores.preferred_skills.total}</div>
            </div>

            <div className="tabs">
                <button
                className={skillsTab === 'covered' ? 'tab active' : 'tab'}
                onClick={() => setSkillsTab('covered')}
                >
                Covered
                </button>
                <button
                className={skillsTab === 'gaps' ? 'tab active' : 'tab'}
                onClick={() => setSkillsTab('gaps')}
                >
                Gaps
                </button>
            </div>

            {skillsTab === 'covered' && (
                <div className="evidence-list">
                {result.explain?.skill_evidence?.filter(s => s.status === 'evidence').map((s, i) => (
                    <div key={i} className="evidence-card">
                        <strong>{s.skill}</strong>
                        {s.resume_quote && <div className="quote">“{s.resume_quote}”</div>}
                    </div>
                    ))}
                </div>
            )}

            {skillsTab === 'gaps' && (
                <div className="evidence-list">
                {result.explain?.gaps?.map((g, i) => (
                    <div key={i} className="gap-card">
                    <strong>{g.label}</strong>
                    <div className="suggestion">{g.suggestion}</div>
                    </div>
                ))}
                </div>
            )}
            <br />
            <hr style={{borderColor: "#475569"}} />
            <br />
            {/* SECTION 3 — Resume Strength */}
            <h3>Resume Strength</h3>

            <ProgressBar label="Clarity" value={result.bars.clarity} />
            <button className="expand" onClick={() => setShowClarity(!showClarity)}>
                {showClarity ? 'Hide details' : 'Show details'}
            </button>

            {showClarity && (
                <ul className="detail-list">
                <li>Structure — {scoreLabel(result.scores.clarity.structure)}</li>
                <li>Signal to Noise — {scoreLabel(result.scores.clarity.signal_to_noise)}</li>
                <li>Verbosity — {scoreLabel(result.scores.clarity.verbosity_control)}</li>
                <li>Actionability — {scoreLabel(result.scores.clarity.actionability)}</li>
                </ul>
            )}

            <ProgressBar label="Signal" value={result.bars.signal} />
            <button className="expand" onClick={() => setShowSignal(!showSignal)}>
                {showSignal ? 'Hide details' : 'Show details'}
            </button>

            {showSignal && (
                <ul className="detail-list">
                <li>Impact Depth — {scoreLabel(result.scores.signal.impact_depth)}</li>
                <li>Quantification — {scoreLabel(result.scores.signal.quantification_quality)}</li>
                <li>Complexity — {scoreLabel(result.scores.signal.complexity_exposure)}</li>
                <li>Ownership — {scoreLabel(result.scores.signal.ownership_level)}</li>
                <li>Originality — {scoreLabel(result.scores.signal.originality)}</li>
                </ul>
            )}

            </div>
        </div>
    </div>
  );
};

const scoreLabel = (n: number) => {
  if (n >= 85) return 'Very strong';
  if (n >= 70) return 'Strong';
  if (n >= 55) return 'Good';
  return 'Needs improvement';
};
